/**
 * Copyright 2026 Perform Content Services Limited. Confidential and Proprietary. All Rights Reserved.
 */
import moment from 'moment';
import {Dispatch} from 'redux';
import {csvParse} from 'd3';

import store, {IAppStore} from 'store';
import {getIngestReports} from 'services/analytics.service';

import {ILineChartData} from 'interfaces';
import {mergeSimilarCSVFiles} from 'components/shared/utils';
import {IPortalError, transformToPortalError} from 'utility/error-handler';

/**
* ACTION TYPES
*/
export const INGEST_FETCHING = 'ingest::INGEST_FETCHING' as const;
export const INGEST_FETCHING_DONE = 'ingest::INGEST_FETCHING_DONE' as const;
export const INGEST_FETCHING_ERROR = 'ingest::INGEST_FETCHING_ERROR' as const;

export const SET_INGEST = 'ingest::SET_INGEST' as const;
export const SET_INGEST_DONE = 'ingest::SET_INGEST_DONE' as const;
export const SET_INGEST_ERROR = 'ingest::SET_INGEST_ERROR' as const;

export const INGEST_SET_CSV = 'ingest::INGEST_SET_CSV' as const;
export const INGEST_SET_QUERY = 'ingest::INGEST_SET_QUERY' as const;

/**
* SELECTORS
*/
export const ingestSelector = ({ingest}: IAppStore): IAppStore['ingest'] => ingest;

/**
* INTERFACES
*/
interface IRequestIngest {
  type: typeof INGEST_FETCHING;
}
interface IReceiveIngest {
  type: typeof INGEST_FETCHING_DONE;
  payload: ILineChartData[];
}
interface IFailedIngest {
  type: typeof INGEST_FETCHING_ERROR;
  error: null | IPortalError;
}

interface IRequestSetIngest {
  type: typeof SET_INGEST;
}
interface IReceiveSetIngest {
  type: typeof SET_INGEST_DONE;
  payload: ILineChartData[];
}
interface IFailedSetIngest {
  type: typeof SET_INGEST_ERROR;
  error: null | Error;
}
interface IIngestSetCSV {
  type: typeof INGEST_SET_CSV;
  payload: string;
}

interface IIngestSetQuery {
  type: typeof INGEST_SET_QUERY;
  payload: {start: Date; end: Date};
}

export type FetchIngestActionsType = IRequestIngest | IReceiveIngest | IFailedIngest;
export type SetIngestActionsType = IRequestSetIngest | IReceiveSetIngest | IFailedSetIngest | IIngestSetCSV | IIngestSetQuery;

interface IFetchIngestActions {
  request: () => FetchIngestActionsType;
  receive: (payload) => FetchIngestActionsType;
  failed: (error: null | IPortalError) => FetchIngestActionsType;
}
interface ISetIngestActions {
  request: () => SetIngestActionsType;
  receive: (payload: ILineChartData[]) => SetIngestActionsType;
  failed: (error: null | Error) => SetIngestActionsType;
  setCSV: (payload: string) => SetIngestActionsType;
  setQuery: (payload: {start: Date; end: Date}) => SetIngestActionsType;
}

/**
* ACTIONS
*/

const fetchIngestActions: IFetchIngestActions = {
  request: () => ({type: INGEST_FETCHING}),
  receive: payload => ({
    type: INGEST_FETCHING_DONE,
    payload
  }),
  failed: error => ({
    type: INGEST_FETCHING_ERROR,
    error
  })
};
const setIngestActions: ISetIngestActions = {
  request: () => ({type: SET_INGEST}),
  receive: payload => ({
    type: SET_INGEST_DONE,
    payload
  }),
  failed: error => ({
    type: SET_INGEST_ERROR,
    error
  }),
  setCSV: payload => ({
    type: INGEST_SET_CSV,
    payload
  }),
  setQuery: payload => ({
    type: INGEST_SET_QUERY,
    payload
  })
};

export const getIngest = data => async(dispatch: Dispatch<FetchIngestActionsType>): Promise<string> => {
  const {
    request,
    failed
  } = fetchIngestActions;

  dispatch(request());

  try {
    return await getIngestReports(data);
  } catch (e) {
    const portalError = transformToPortalError(e);

    dispatch(failed(portalError));

    throw portalError;
  }
};

export const setIngest = (data: string) => async(dispatch: Dispatch<SetIngestActionsType>): Promise<void> => {
  const {
    request,
    receive,
    failed,
    setCSV
  } = setIngestActions;

  dispatch(request());

  try {
    const state = store.getState();
    const {csv} = ingestSelector(state);
    const csvFile = csv ? mergeSimilarCSVFiles(data, csv) : data;

    dispatch(setCSV(csvFile));

    const ingestReports = csvParse(data)
      .slice(0, -1) // Remove last element
      .map(item => ([
        {
          x: moment.utc(item.StartOfUnderrun).subtract(100, 'milliseconds').toDate(),
          y: 0
        },
        {
          x: moment.utc(item.StartOfUnderrun).toDate(),
          y: Number(item.DurationMillisecond)
        },
        {
          x: moment.utc(item.EndOfUnderrun).toDate(),
          y: Number(item.DurationMillisecond)
        },
        {
          x: moment.utc(item.EndOfUnderrun).add(100, 'milliseconds').toDate(),
          y: 0
        }
      ]))
      .flat(Infinity) as ILineChartData[];

    dispatch(receive(ingestReports));
  } catch (e) {
    const portalError = transformToPortalError(e);

    dispatch(failed(portalError));

    throw portalError;
  }
};

export const setIngestQuery = (query: {start: Date; end: Date}) => async(dispatch: Dispatch<SetIngestActionsType>): Promise<void> => {
  const {
    request,
    setQuery,
    failed
  } = setIngestActions;

  dispatch(request());

  try {
    dispatch(setQuery(query));
  } catch (e) {
    const portalError = transformToPortalError(e);

    dispatch(failed(portalError));

    throw portalError;
  }
};

export type IngestActions = FetchIngestActionsType | SetIngestActionsType;